// External API consumers and the keys issued to them.
//
// This is the ISSUING side: register a platform, choose which data domains its
// key may read, mint the key, hand it over once, revoke it when the relationship
// ends. The full key is shown in a one-time dialog and is unrecoverable
// afterwards — the server only stores its SHA-256 hash.
//
// The data picker mirrors the SoftwareWatch gateway catalog: a key only reaches
// the domains/resources selected here, and its platform calls
// /api/software-watch/v1/{domain}/{resource} with the issued key.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  KeyRound, Plus, Copy, Trash2, Info, Loader2, Check, Building2, Ban,
  ChevronDown, ChevronRight, AlertTriangle, Power, Search, Globe, Lock,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Modal from "@/components/dashboard/Modal";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import EmptyState from "@/components/shared/EmptyState";
import LoadError from "@/components/dashboard/LoadError";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/dates";
import { useApiData } from "@/hooks/useApiData";
import {
  listConsumers, updateConsumer, deleteConsumer, listConsumerKeys,
  issueApiKey, revokeApiKey, listApiDomains, createPlatformWithKey, listCompanies,
} from "@/api/admin";

const fmtDate = (v) => formatDate(v, "never");

const STATUS_CLS = {
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300",
  revoked: "bg-rose-500/15 text-rose-700 dark:text-rose-300",
  expired: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
  disabled: "bg-slate-200 text-slate-600 dark:bg-white/10 dark:text-slate-300",
};

// ---------------------------------------------------------------------------
// DataPicker — the domain-grouped "what data can this platform access?" control.
//
// `groups` is the catalog from GET /admin/api-domains. A selection is one of
// three grants, and the distinction matters after the key is issued:
//
//   * "Everything"  → the "*" wildcard: every domain, including ones added later.
//   * a DOMAIN      → that domain's own scope slug, which also covers resources
//                     added to it later. Ticking the domain card grants this.
//   * a RESOURCE    → exactly one "domain:resource" scope and nothing else.
//
// Ticking a domain therefore REPLACES its individual resource picks rather than
// adding to them — holding both would be redundant and would misreport what the
// key can actually reach.
// ---------------------------------------------------------------------------
function DataPicker({ groups, everything, picked, onChange }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(() => new Set());

  const pickedSet = useMemo(() => new Set(picked), [picked]);
  const q = query.trim().toLowerCase();

  // Filter domains/resources by the search query (matches domain or resource text).
  const filtered = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        domains: g.domains
          .map((d) => {
            const domainHit = (d.name + " " + d.slug + " " + (d.description || "")).toLowerCase().includes(q);
            const resources = domainHit
              ? d.resources
              : d.resources.filter((r) => (r.name + " " + r.slug + " " + (r.description || "")).toLowerCase().includes(q));
            return resources.length ? { ...d, resources } : null;
          })
          .filter(Boolean),
      }))
      .filter((g) => g.domains.length);
  }, [groups, q]);

  const totalSelected = pickedSet.size;

  function toggleEverything() {
    // The wildcard is standalone: turning it on clears everything below, so the
    // selection can never claim to be narrower than the key really is.
    onChange({ everything: !everything, picked: everything ? picked : [] });
  }

  function toggleResource(domain, scope) {
    if (pickedSet.has(domain.scope)) return;   // already covered by the domain grant
    const next = new Set(pickedSet);
    if (next.has(scope)) next.delete(scope); else next.add(scope);
    onChange({ everything, picked: [...next] });
  }

  function toggleDomain(domain) {
    const next = new Set(pickedSet);
    if (next.has(domain.scope)) {
      next.delete(domain.scope);
    } else {
      next.add(domain.scope);
      // Its individual resource picks are now redundant.
      domain.resources.forEach((r) => next.delete(r.scope));
    }
    onChange({ everything, picked: [...next] });
  }

  function toggleOpen(slug) {
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(slug)) n.delete(slug); else n.add(slug);
      return n;
    });
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          What data can this platform access?
        </span>
        <span className={cn(
          "shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold",
          everything || totalSelected
            ? "bg-brand-500/15 text-brand-700 dark:text-brand-300"
            : "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400",
        )}>
          {everything ? "Everything" : `${totalSelected} selected`}
        </span>
      </div>

      <div className="relative mb-3">
        <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search data…"
          className="w-full rounded-xl border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-800 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-500/20 dark:border-white/10 dark:bg-white/5 dark:text-white"
        />
      </div>

      {/* Everything */}
      <label className={cn(
        "mb-4 flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors",
        everything
          ? "border-brand-300 bg-brand-500/5 dark:border-brand-500/40"
          : "border-slate-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5",
      )}>
        <input type="checkbox" checked={everything} onChange={toggleEverything}
          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-white/20 dark:bg-white/5" />
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300">
          <Globe size={17} />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-slate-800 dark:text-white">Everything</span>
          <span className="block text-xs text-slate-400">All domains — full read access, including domains added later.</span>
        </span>
      </label>

      <div className={cn("space-y-5", everything && "pointer-events-none opacity-50")}>
        {filtered.map((g) => (
          <div key={g.group}>
            <p className="mb-2 text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">{g.group}</p>
            <div className="space-y-2">
              {g.domains.map((d) => {
                const wholeDomain = pickedSet.has(d.scope);
                const scopes = d.resources.map((r) => r.scope);
                const total = scopes.length;
                const sel = wholeDomain ? total : scopes.filter((s) => pickedSet.has(s)).length;
                const isOpen = open.has(d.slug) || !!q;
                return (
                  <div key={d.slug} className="overflow-hidden rounded-xl border border-slate-200 dark:border-white/10">
                    <div className="flex items-center gap-3 p-3">
                      <input
                        type="checkbox"
                        checked={wholeDomain}
                        ref={(el) => { if (el) el.indeterminate = !wholeDomain && sel > 0; }}
                        onChange={() => toggleDomain(d)}
                        title="Grant the whole domain, including resources added to it later"
                        className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 dark:border-white/20 dark:bg-white/5"
                      />
                      <button type="button" onClick={() => toggleOpen(d.slug)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-semibold text-slate-800 dark:text-white">{d.name}</span>
                          <span className="block truncate font-mono text-[0.6875rem] text-slate-400">{d.slug}</span>
                        </span>
                      </button>
                      <span className={cn(
                        "shrink-0 rounded-full px-2 py-0.5 text-[0.6875rem] font-semibold",
                        sel ? "bg-brand-500/15 text-brand-700 dark:text-brand-300" : "bg-slate-100 text-slate-500 dark:bg-white/5 dark:text-slate-400",
                      )}>{sel}/{total}</span>
                      <button type="button" onClick={() => toggleOpen(d.slug)} className="shrink-0 text-slate-400">
                        {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </button>
                    </div>
                    {isOpen && (
                      <div className="space-y-1 border-t border-slate-100 bg-slate-50/60 p-3 dark:border-white/5 dark:bg-white/[0.02]">
                        {d.resources.map((r) => (
                          <label key={r.scope} className="flex cursor-pointer items-start gap-2.5 rounded-lg p-1.5 hover:bg-white dark:hover:bg-white/5">
                            <input
                              type="checkbox"
                              checked={wholeDomain || pickedSet.has(r.scope)}
                              disabled={wholeDomain}
                              onChange={() => toggleResource(d, r.scope)}
                              className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500/30 disabled:opacity-60 dark:border-white/20 dark:bg-white/5"
                            />
                            <span className="min-w-0">
                              <span className="flex items-center gap-1.5">
                                <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{r.name}</span>
                                {r.pii && (
                                  <span title="Contains personal data (names, emails)" className="inline-flex items-center gap-0.5 rounded bg-amber-500/15 px-1 py-0.5 text-[0.625rem] font-semibold text-amber-700 dark:text-amber-300">
                                    <Lock size={9} /> PII
                                  </span>
                                )}
                              </span>
                              <span className="block text-xs text-slate-400">{r.description}</span>
                            </span>
                          </label>
                        ))}
                        {wholeDomain && (
                          <p className="px-1.5 pt-1 text-[0.6875rem] text-slate-400">
                            The whole domain is granted — these stay selected, and any resource
                            added to <span className="font-mono">{d.slug}</span> later is included too.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="py-6 text-center text-sm text-slate-400">No data matches “{query}”.</p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Advanced — collapsed by default because most keys need neither option. An IP
// allowlist is the difference between "a leaked key works anywhere" and "a
// leaked key works from one office", so it is worth offering even though it is
// rarely used.
// ---------------------------------------------------------------------------
function AdvancedOptions({ expiryDays, onExpiry, allowedIps, onAllowedIps }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 p-3 text-left text-sm font-medium text-slate-600 dark:text-slate-300"
      >
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        Advanced
        <span className="text-xs font-normal text-slate-400">expiry · IP allowlist</span>
      </button>
      {open && (
        <div className="space-y-4 border-t border-slate-100 p-3 dark:border-white/5">
          <Input
            label="Expires in (days)"
            type="number"
            min="1"
            placeholder="Leave blank for no expiry"
            value={expiryDays}
            onChange={(e) => onExpiry(e.target.value)}
          />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
              IP allowlist (optional)
            </label>
            <textarea
              rows={3}
              value={allowedIps}
              onChange={(e) => onAllowedIps(e.target.value)}
              placeholder={"203.0.113.7\n10.0.0.0/8"}
              className="w-full rounded-xl border border-slate-200 bg-white p-3 font-mono text-xs text-slate-800 outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-500/20 dark:border-white/10 dark:bg-white/5 dark:text-white"
            />
            <p className="mt-1.5 text-xs text-slate-400">
              One address or CIDR block per line. Leave it empty to allow any address — a
              non-empty list means every other address is refused even with a valid key.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One-time key reveal panel — shared by both flows.
// ---------------------------------------------------------------------------
function KeyReveal({ apiKey }) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const codeRef = useRef(null);
  async function copy() {
    setCopyFailed(false);
    try {
      // There is no clipboard API at all outside a secure context, so a plain
      // http:// deployment throws here — and this is the only time the key is
      // shown, so the failure must never be silent.
      if (!navigator.clipboard?.writeText) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      const selection = window.getSelection?.();
      if (selection && codeRef.current) {
        selection.removeAllRanges();
        selection.selectAllChildren(codeRef.current);
      }
      setCopyFailed(true);
    }
  }
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-800 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
        <AlertTriangle size={16} className="mt-0.5 shrink-0" />
        <span>This is the only time this key is shown. It isn&apos;t stored anywhere we can read — if it&apos;s lost, revoke it and issue a new one.</span>
      </div>
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-white/5">
        <code ref={codeRef} className="flex-1 break-all font-mono text-sm text-slate-800 dark:text-white">{apiKey}</code>
        <button type="button" onClick={copy} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-slate-500 hover:bg-slate-200 dark:hover:bg-white/10">
          {copied ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
        </button>
      </div>
      {copyFailed && (
        <p className="text-xs font-medium text-amber-700 dark:text-amber-300">
          Copy failed — this browser blocks clipboard access here. The key is selected: press Ctrl+C (⌘C).
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// One consumer row — expands to show its keys.
// ---------------------------------------------------------------------------
function ConsumerRow({ consumer, refreshToken, onChanged, onIssued, onError }) {
  const [open, setOpen] = useState(false);
  const [keys, setKeys] = useState(null);
  const [keysError, setKeysError] = useState(null);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [busy, setBusy] = useState(false);
  // Deleting a platform revokes every key it holds and breaks whatever is
  // calling the gateway with them. It used to fire on one unconfirmed click.
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Revoking is just as terminal — there is no un-revoke endpoint — so it gets
  // the same confirmation rather than firing on one click of the wrong row.
  const [confirmingRevoke, setConfirmingRevoke] = useState(null);

  const loadKeys = useCallback(async () => {
    setLoadingKeys(true);
    setKeysError(null);
    try {
      setKeys(await listConsumerKeys(consumer.id));
    } catch (e) {
      // Kept separate from `keys` so a failed fetch never renders as "no keys
      // issued yet" for a platform that may hold live credentials.
      setKeysError((e && e.message) || "Could not load keys.");
    } finally {
      setLoadingKeys(false);
    }
  }, [consumer.id]);

  useEffect(() => { if (open && keys === null) loadKeys(); }, [open, keys, loadKeys]);

  // A key issued from the page-level dialog leaves this row's cached list stale;
  // the parent bumps the token so it re-fetches instead of hiding the new key.
  useEffect(() => { setKeys(null); setKeysError(null); }, [refreshToken]);

  async function toggleEnabled() {
    setBusy(true);
    try {
      await updateConsumer(consumer.id, { name: consumer.name, enabled: !consumer.enabled });
      await onChanged();
    } catch (e) {
      onError((e && e.message) || "Could not update the consumer.");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await deleteConsumer(consumer.id);
      setConfirmingDelete(false);
      await onChanged();
    } catch (e) {
      // Keep the dialog open so the reason stays on screen next to the action.
      onError((e && e.message) || "Could not delete the consumer.");
    } finally {
      setBusy(false);
    }
  }

  async function revoke(keyId) {
    setBusy(true);
    try {
      await revokeApiKey(keyId);
      setConfirmingRevoke(null);
      await loadKeys();
      await onChanged();
    } catch (e) {
      onError((e && e.message) || "Could not revoke the key.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={cn("card overflow-hidden", !consumer.enabled && "opacity-70")}>
      <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex min-w-0 flex-1 items-center gap-3 text-left">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
            <Building2 size={18} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold text-slate-800 dark:text-white">{consumer.name}</p>
              {!consumer.enabled && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-300">
                  <Ban size={11} /> Disabled
                </span>
              )}
              {consumer.unscoped && (
                <span
                  title="No company bound — every request this platform makes returns nothing."
                  className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                >
                  <AlertTriangle size={11} /> Unscoped — returns no data
                </span>
              )}
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500 dark:bg-white/5 dark:text-slate-400">
                {consumer.key_count} active {consumer.key_count === 1 ? "key" : "keys"}
              </span>
            </div>
            <p className="mt-0.5 truncate text-xs text-slate-400">
              {consumer.description || "No description"}
              {consumer.contact_email ? ` · ${consumer.contact_email}` : ""}
            </p>
          </div>
          {open ? <ChevronDown size={16} className="shrink-0 text-slate-400" /> : <ChevronRight size={16} className="shrink-0 text-slate-400" />}
        </button>

        <div className="flex shrink-0 items-center gap-2">
          <Button size="sm" onClick={() => onIssued(consumer)} disabled={!consumer.enabled}>
            <Plus size={15} /> Issue key
          </Button>
          <button type="button" onClick={toggleEnabled} disabled={busy}
            title={consumer.enabled ? "Disable — cuts off every key it holds" : "Enable"}
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-60 dark:hover:bg-white/10">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Power size={15} />}
          </button>
          <button type="button" onClick={() => setConfirmingDelete(true)} disabled={busy}
            title="Delete — revokes every key it holds"
            className="grid h-9 w-9 place-items-center rounded-lg text-rose-500 hover:bg-rose-50 disabled:opacity-60 dark:hover:bg-rose-500/10">
            <Trash2 size={15} />
          </button>

          <Modal
            open={confirmingDelete}
            onClose={() => setConfirmingDelete(false)}
            title={`Delete ${consumer.name}?`}
            size="md"
            footer={
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setConfirmingDelete(false)}>Cancel</Button>
                <Button variant="danger" size="sm" onClick={remove} disabled={busy}>
                  {busy ? <Loader2 size={15} className="animate-spin" /> : "Delete platform"}
                </Button>
              </div>
            }
          >
            <p className="text-sm text-slate-600 dark:text-slate-300">
              This revokes every API key issued to <span className="font-medium">{consumer.name}</span> immediately.
              Any integration still calling the gateway with one will start failing. This cannot be undone.
            </p>
          </Modal>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-100 bg-slate-50/60 p-4 dark:border-white/5 dark:bg-white/[0.02]">
          {loadingKeys ? (
            <div className="flex justify-center py-6 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>
          ) : keysError ? (
            <LoadError message={keysError} onRetry={loadKeys} />
          ) : !keys || keys.length === 0 ? (
            <p className="py-4 text-center text-sm text-slate-400">
              No keys issued yet. Issue one to let this platform call the API.
            </p>
          ) : (
            <div className="space-y-2">
              {keys.map((k) => (
                <div key={k.id} className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-white/5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-slate-800 dark:text-white">{k.label}</p>
                      <span className={cn("rounded-full px-2 py-0.5 text-xs font-semibold capitalize", STATUS_CLS[k.status] || STATUS_CLS.disabled)}>
                        {k.status}
                      </span>
                    </div>
                    <p className="mt-1 font-mono text-xs text-slate-400">{k.masked}</p>
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {(k.scopes || []).map((s) => (
                        <span key={s} className="rounded bg-sky-500/10 px-1.5 py-0.5 font-mono text-[0.6875rem] text-sky-700 dark:text-sky-300">
                          {s === "*" ? "everything" : s}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-4">
                    <div className="text-right text-xs text-slate-400">
                      <p>Used {k.usage_count}× · last {fmtDate(k.last_used_at)}</p>
                      <p>{k.expires_at ? `Expires ${fmtDate(k.expires_at)}` : "No expiry"}</p>
                      {(k.allowed_ips || []).length > 0 && (
                        <p title="This key only works from these addresses">
                          IPs: {(k.allowed_ips || []).join(", ")}
                        </p>
                      )}
                    </div>
                    {k.status !== "revoked" && (
                      <button type="button" onClick={() => setConfirmingRevoke(k)} disabled={busy}
                        title="Revoke — takes effect on the next request, permanently"
                        className="h-8 rounded-lg border border-rose-200 px-2.5 text-xs font-semibold text-rose-600 hover:bg-rose-50 disabled:opacity-60 dark:border-rose-500/30 dark:hover:bg-rose-500/10">
                        Revoke
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        open={!!confirmingRevoke}
        onClose={() => setConfirmingRevoke(null)}
        title={confirmingRevoke ? `Revoke ${confirmingRevoke.label}?` : "Revoke key"}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setConfirmingRevoke(null)}>Cancel</Button>
            <Button variant="danger" size="sm" onClick={() => revoke(confirmingRevoke.id)} disabled={busy}>
              {busy ? <Loader2 size={15} className="animate-spin" /> : "Revoke key"}
            </Button>
          </div>
        }
      >
        <p className="text-sm text-slate-600 dark:text-slate-300">
          <span className="font-mono">{confirmingRevoke?.masked}</span> stops working on the next
          request, and anything calling the gateway with it starts failing. A revoked key cannot be
          restored — issue a new one instead.
        </p>
      </Modal>
    </div>
  );
}

// ---------------------------------------------------------------------------
export default function ApiKeys() {
  const { data, loading, error, reload } = useApiData(listConsumers, []);
  // The data-catalog fetch dropped `error`, so a failed load rendered the
  // scope picker as "no domains available" and a key could be issued with an
  // empty selection that looked deliberate.
  const {
    data: domainData, loading: domainLoading, demo: domainsDemo,
    error: domainError, reload: reloadDomains,
  } = useApiData(listApiDomains, null);
  const consumers = Array.isArray(data) ? data : [];
  const groups = domainData?.groups || [];
  const everythingScope = domainData?.everything_scope || "*";
  // A backend that is simply unreachable sets `demo` and leaves `domainError`
  // null, so both have to count as "the catalog did not load" — otherwise the
  // dialogs spin on an empty list with nothing to retry.
  const domainLoadFailed = !!domainError || domainsDemo;

  const [toast, setToast] = useState(null);

  // create-platform modal (single step: platform + first key)
  const [createOpen, setCreateOpen] = useState(false);
  const [cName, setCName] = useState("");
  const [cDesc, setCDesc] = useState("");
  const [cSel, setCSel] = useState({ everything: false, picked: [] });
  // Which tenant's data this platform reads THROUGH THE PUBLIC API. Optional,
  // because the two surfaces mean different things by "unbound":
  //   * /api/public/v1 fails closed — an unbound consumer reads nothing.
  //   * the SoftwareWatch gateway is portfolio-wide by design and ignores this
  //     binding entirely; a monitoring platform is supposed to see every tenant.
  // Forcing a company here would have made a gateway-only platform look scoped
  // to one customer when it is not.
  const [cCompany, setCCompany] = useState("");
  const [cExpiry, setCExpiry] = useState("");
  const [cIps, setCIps] = useState("");
  const [companies, setCompanies] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);

  // issue-key modal (additional key for an existing platform)
  const [issueFor, setIssueFor] = useState(null);
  const [label, setLabel] = useState("");
  const [iSel, setISel] = useState({ everything: false, picked: [] });
  const [expiryDays, setExpiryDays] = useState("");
  const [iIps, setIIps] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [issuedKey, setIssuedKey] = useState(null);
  // Bumped per consumer after a key is issued: an expanded row caches its key
  // list, and reload() alone refreshes only the header count, not that list.
  const [keysVersion, setKeysVersion] = useState({});

  const selectionScopes = (sel) => (sel.everything ? [everythingScope] : sel.picked);
  const hasSelection = (sel) => sel.everything || sel.picked.length > 0;
  // One entry per line (commas tolerated); blanks dropped. The server
  // validates each one and REJECTS a typo rather than ignoring it — an
  // ignored typo would silently lock the platform out of its own data.
  const parseIps = (text) => text.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

  function closeCreate() {
    setCreateOpen(false);
    setCName(""); setCDesc(""); setCSel({ everything: false, picked: [] });
    setCCompany(""); setCExpiry(""); setCIps(""); setCreatedKey(null);
  }

  // Load the tenant list lazily — only the create modal needs it.
  useEffect(() => {
    if (!createOpen || companies.length) return;
    listCompanies()
      .then((rows) => setCompanies(Array.isArray(rows) ? rows : rows?.items || []))
      // Swallowing this made a failed fetch look like an empty tenant
      // directory, so the admin bound nothing and got an unscoped platform.
      .catch((e) => setToast((e && e.message) || "Could not load the company list."));
  }, [createOpen, companies.length]);

  async function createPlatform() {
    if (!cName.trim() || !hasSelection(cSel)) return;
    setCreating(true);
    try {
      const res = await createPlatformWithKey({
        name: cName.trim(),
        description: cDesc.trim() || null,
        company_id: cCompany || null,
        scopes: selectionScopes(cSel),
        expires_in_days: cExpiry ? Number(cExpiry) : null,
        allowed_ips: parseIps(cIps),
      });
      setCreatedKey(res.key.api_key); // one and only reveal
      await reload();
    } catch (e) {
      setToast((e && e.message) || "Could not create the platform.");
    } finally {
      setCreating(false);
    }
  }

  // Both dialogs show the same catalog, and both used to render an endless
  // spinner whenever it failed — the only escape was a full page reload.
  function renderDataPicker(sel, onChange) {
    if (domainLoading) {
      return <div className="flex justify-center py-8 text-slate-400"><Loader2 size={20} className="animate-spin" /></div>;
    }
    if (domainLoadFailed) {
      return (
        <LoadError
          message={domainError || "Could not load the data catalog."}
          onRetry={reloadDomains}
        />
      );
    }
    if (groups.length === 0) {
      return <p className="py-6 text-center text-sm text-slate-400">No data domains are available to grant.</p>;
    }
    return <DataPicker groups={groups} everything={sel.everything} picked={sel.picked} onChange={onChange} />;
  }

  function openIssue(consumer) {
    setIssueFor(consumer);
    setLabel(""); setISel({ everything: false, picked: [] });
    setExpiryDays(""); setIIps(""); setIssuedKey(null);
  }

  async function issue() {
    if (!label.trim() || !hasSelection(iSel)) return;
    setIssuing(true);
    try {
      const res = await issueApiKey(issueFor.id, {
        label: label.trim(),
        scopes: selectionScopes(iSel),
        expires_in_days: expiryDays ? Number(expiryDays) : null,
        allowed_ips: parseIps(iIps),
      });
      setIssuedKey(res.api_key);
      setKeysVersion((v) => ({ ...v, [issueFor.id]: (v[issueFor.id] || 0) + 1 }));
      await reload();
    } catch (e) {
      // Closing the dialog here threw away the label, scope selection, expiry
      // and IP allow-list the admin had just filled in, so a transient failure
      // meant re-entering the whole form. Leave it open to retry.
      setToast((e && e.message) || "Could not issue the key.");
    } finally {
      setIssuing(false);
    }
  }

  return (
    <>
      <PageHeader
        title="API Access"
        subtitle="External platforms that can read your data through the gateway — you choose which data each one gets."
        actions={<Button size="sm" onClick={() => setCreateOpen(true)}><Plus size={16} /> New data platform</Button>}
      />

      {(toast || error || domainError) && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/70 px-4 py-2.5 text-sm text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
          <AlertTriangle size={15} className="shrink-0" />
          <span className="flex-1">{toast || error || domainError}</span>
          {/* A load error is only cleared by a successful reload, so dismissing
              it would leave the ✕ inert — offer the retry that can clear it. */}
          {toast ? (
            <button type="button" onClick={() => setToast(null)} className="text-xs font-medium opacity-60 hover:opacity-100">✕</button>
          ) : (
            <button type="button" onClick={() => (error ? reload() : reloadDomains())} className="text-xs font-medium underline opacity-80 hover:opacity-100">
              Retry
            </button>
          )}
        </div>
      )}

      <div className="mb-5 flex items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50/70 p-4 dark:border-sky-500/20 dark:bg-sky-500/10">
        <Info size={18} className="mt-0.5 shrink-0 text-sky-500" />
        <p className="text-sm text-sky-800 dark:text-sky-200">
          Register a platform and pick the data it may read. Its software calls{" "}
          <code className="rounded bg-sky-100 px-1 dark:bg-sky-500/20">/api/software-watch/v1/…</code> with{" "}
          <code className="rounded bg-sky-100 px-1 dark:bg-sky-500/20">Authorization: Bearer &lt;key&gt;</code>.
          Keys are shown once and stored only as a hash — if one is lost, revoke it and issue another.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : consumers.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No platforms yet"
          message="Register the first external platform that should be able to read your data."
          action={<Button onClick={() => setCreateOpen(true)}><Plus size={16} /> New data platform</Button>}
        />
      ) : (
        <div className="space-y-3">
          {consumers.map((c) => (
            <ConsumerRow key={c.id} consumer={c} refreshToken={keysVersion[c.id] || 0}
              onChanged={reload} onIssued={openIssue} onError={setToast} />
          ))}
        </div>
      )}

      {/* new data platform — platform + first key in one step */}
      <Modal
        open={createOpen}
        onClose={closeCreate}
        title={createdKey ? "Copy this key now" : "New data platform"}
        size="xl"
        footer={
          <div className="flex justify-end gap-2">
            {createdKey ? (
              <Button size="sm" onClick={closeCreate}>Done</Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={closeCreate}>Cancel</Button>
                <Button size="sm" onClick={createPlatform} disabled={creating || !cName.trim() || !hasSelection(cSel)}>
                  {creating ? <Loader2 size={15} className="animate-spin" /> : "Create + generate key"}
                </Button>
              </>
            )}
          </div>
        }
      >
        {createdKey ? (
          <KeyReveal apiKey={createdKey} />
        ) : (
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Platform name" placeholder="e.g. SoftwareWatch, Analytics-BI" value={cName} onChange={(e) => setCName(e.target.value)} />
              <Input label="Description (optional)" placeholder="What does it use the API for?" value={cDesc} onChange={(e) => setCDesc(e.target.value)} />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Whose data can it read? <span className="font-normal text-slate-400">(public API only)</span>
              </label>
              <select
                value={cCompany}
                onChange={(e) => setCCompany(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-brand-400 dark:border-white/10 dark:bg-slate-900 dark:text-white"
              >
                <option value="">Every company (monitoring platform)</option>
                {companies.map((co) => (
                  <option key={co.id} value={co.id}>{co.name}</option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-slate-400">
                Bind a company and this platform reads that tenant and nothing else through
                <code className="mx-1">/api/public/v1</code> — leave it unset and that API
                returns nothing for it. The SoftwareWatch gateway is portfolio-wide either
                way: a monitoring platform is meant to see every tenant, so leave this unset
                for one.
              </p>
            </div>
            {renderDataPicker(cSel, setCSel)}
            <AdvancedOptions expiryDays={cExpiry} onExpiry={setCExpiry}
              allowedIps={cIps} onAllowedIps={setCIps} />
          </div>
        )}
      </Modal>

      {/* issue an additional key for an existing platform */}
      <Modal
        open={!!issueFor}
        onClose={() => setIssueFor(null)}
        title={issuedKey ? "Copy this key now" : `Issue a key for ${issueFor?.name || ""}`}
        size="xl"
        footer={
          <div className="flex justify-end gap-2">
            {issuedKey ? (
              <Button size="sm" onClick={() => setIssueFor(null)}>Done</Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setIssueFor(null)}>Cancel</Button>
                <Button size="sm" onClick={issue} disabled={issuing || !label.trim() || !hasSelection(iSel)}>
                  {issuing ? <Loader2 size={15} className="animate-spin" /> : "Issue key"}
                </Button>
              </>
            )}
          </div>
        }
      >
        {issuedKey ? (
          <KeyReveal apiKey={issuedKey} />
        ) : (
          <div className="space-y-5">
            <Input label="Key label" placeholder="e.g. Production, Monitoring bot" value={label} onChange={(e) => setLabel(e.target.value)} />
            {renderDataPicker(iSel, setISel)}
            <AdvancedOptions expiryDays={expiryDays} onExpiry={setExpiryDays}
              allowedIps={iIps} onAllowedIps={setIIps} />
          </div>
        )}
      </Modal>
    </>
  );
}
